import { useState, useMemo } from "react";
import DialogContent from "@mui/material/DialogContent";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { DialogActions, useTheme } from "@mui/material";
import { ModalTitle, ModalMessage, ModalButton, ModalErrorText, ModalWarningText } from "../DialogModalComponents";
import { EventModalTypes } from "../DialogModalWrapper";
import { ICrewEvent, isIDisplayPairing } from "src/redux/reducers/CrewScheduleReducer";
import { deletePairing } from "src/redux/actions/CrewSchedule/pairings";
import { setSelectedCrewEvents } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
// ModalWarningText
import { findMosaicNode, IMosaicNode } from "../../../utils/MosaicTree";
import { DialogModalStyles } from "../DialogModalWrapper";

interface ISplitPairing {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

/**
 *  Modal to delete actively selected pairing
 */
export const DeletePairingModal = (props: ISplitPairing): JSX.Element => {
  const { isOpen, handleClose, setActiveEventModal } = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  const selectedCrewEvents: ICrewEvent[] = useSelector((state: IRootState) => state.crewSchedule.selectedCrewEvents);

  // Return selected pairing Id.
  const pairingId: string = useMemo(() => {
    const events: ICrewEvent[] = selectedCrewEvents.filter(
      (evnt: ICrewEvent) => isIDisplayPairing(evnt.event) && !evnt.isPlanned
    );
    return events[0]?.event?._id;
  }, [selectedCrewEvents]);
  const invalidPairingError = !pairingId ? "Selected event is not a valid pairing." : null;

  // Check to see if any editors are open containing this pairing id.
  const currentNode: IMosaicNode = useSelector((state: IRootState) => state.windowManager.currentNode);
  const openEditor = useMemo(() => {
    return findMosaicNode(currentNode, [], "PairingEditorWindow", { pairingId });
  }, [currentNode, pairingId]);
  const openEditorError = openEditor
    ? "Selected pairing has an open editor window, please close it before deleting."
    : null;

  const [apiError, setApiError] = useState<string>(null);

  //////////////
  // HANDLERS //
  //////////////
  const handleConfirm = async (): Promise<void> => {
    if (!pairingId) {
      return;
    }

    try {
      const response = await deletePairing(pairingId, dispatch);
      if (response?.deleted) {
        // Reset and close modal.
        handleClose(false);
        setActiveEventModal(EventModalTypes.None);
        dispatch(setSelectedCrewEvents([], false, false, false));
      }
    } catch (error) {
      // TODO: Determine error text/typing properly.
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      setApiError(error.toString());
    }
  };

  const handleCancel = (): void => {
    //cancel modal
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  if (isOpen) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content" style={{ display: "flex", maxWidth: 500 }}>
          <ModalTitle title="Delete Pairing" />
          <ModalMessage message="Are you certain you wish to delete this pairing? This operation cannot be undone." />

          {openEditorError && <ModalWarningText warning={openEditorError} />}
          {invalidPairingError && <ModalErrorText error={invalidPairingError} />}
          {apiError && <ModalErrorText error={apiError} />}

          <DialogActions>
            <ModalButton label="Cancel" handler={handleCancel}></ModalButton>
            <ModalButton
              label="Delete Pairing"
              disable={!!invalidPairingError || !!openEditorError || !!apiError}
              handler={handleConfirm}
            ></ModalButton>
          </DialogActions>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
